import { X } from 'lucide-react';
import type { EntityCrossFilter, EntityGraph } from '@/utils/entityCrossFilter';
import { normalizeCrossFilter } from '@/utils/entityCrossFilter';
import { AD_PRODUCT_METRIC_ROWS } from '@/utils/workspaceAdProductMetrics';
import { TARGETING_TYPE_ROWS } from '@/utils/workspaceTargetingTypeMetrics';
import { MATCH_TYPE_ROWS } from '@/utils/workspaceMatchTypeMetrics';
import { PLACEMENT_ROWS } from '@/utils/workspacePlacementMetrics';
import { AUDIENCE_SEGMENT_ROWS } from '@/utils/workspaceAudienceMetrics';

type Chip = {
  dimension: keyof EntityCrossFilter;
  id: string;
  label: string;
  unavailable?: boolean;
};

const DIMENSION_PREFIX: Record<keyof EntityCrossFilter, string> = {
  portfolioIds: 'Portfolio',
  campaignKeys: 'Campaign',
  adGroupIds: 'Ad group',
  keywordIds: 'Keyword',
  adProducts: 'Ad product',
  targetingTypes: 'Targeting',
  matchTypes: 'Match type',
  placements: 'Placement',
  audienceSegments: 'Audience',
  searchTermKeys: 'Search term',
  asins: 'ASIN',
  adProductRowKeys: 'Ad product row',
};

function labelFromRows(rows: Array<{ id: string; label: string }>, id: string): string | null {
  return rows.find((r) => r.id === id)?.label ?? null;
}

function buildChips(graph: EntityGraph, filter: EntityCrossFilter): Chip[] {
  const f = normalizeCrossFilter(filter);
  const chips: Chip[] = [];

  for (const portfolioId of f.portfolioIds) {
    const row = graph.portfolios.find((p) => p.portfolioId === portfolioId);
    chips.push({
      dimension: 'portfolioIds',
      id: portfolioId,
      label: row?.name ?? portfolioId,
      unavailable: !row,
    });
  }

  for (const campaignKey of f.campaignKeys) {
    const row = graph.indices.campaignsByKey.get(campaignKey);
    chips.push({
      dimension: 'campaignKeys',
      id: campaignKey,
      label: row ? `${row.name} (${row.adProduct})` : campaignKey,
      unavailable: !row,
    });
  }

  for (const adGroupId of f.adGroupIds) {
    const row = graph.indices.adGroupsById.get(adGroupId);
    chips.push({
      dimension: 'adGroupIds',
      id: adGroupId,
      label: row?.name ?? adGroupId,
      unavailable: !row,
    });
  }

  for (const keywordId of f.keywordIds) {
    let label = keywordId;
    let found = false;
    for (const [groupKey, pins] of graph.indices.keywordPinsByGroupKey.entries()) {
      const pin = pins.find((p) => p.keywordId === keywordId);
      if (!pin) continue;
      found = true;
      const row = graph.keywords.find((k) => k.groupKey === groupKey);
      label = row?.keyword_text ?? keywordId;
      break;
    }
    chips.push({
      dimension: 'keywordIds',
      id: keywordId,
      label,
      unavailable: !found,
    });
  }

  for (const id of f.adProducts) {
    const fromRows = AD_PRODUCT_METRIC_ROWS.find(
      (r) => r.label === id || r.reportId === id
    );
    chips.push({
      dimension: 'adProducts',
      id,
      label: fromRows?.label ?? id,
    });
  }

  for (const id of f.targetingTypes) {
    chips.push({
      dimension: 'targetingTypes',
      id,
      label: labelFromRows(TARGETING_TYPE_ROWS, id) ?? id,
    });
  }

  for (const id of f.matchTypes) {
    chips.push({
      dimension: 'matchTypes',
      id,
      label: labelFromRows(MATCH_TYPE_ROWS, id) ?? id,
    });
  }

  for (const id of f.placements) {
    chips.push({
      dimension: 'placements',
      id,
      label: labelFromRows(PLACEMENT_ROWS, id) ?? id,
    });
  }

  for (const id of f.audienceSegments) {
    chips.push({
      dimension: 'audienceSegments',
      id,
      label: labelFromRows(AUDIENCE_SEGMENT_ROWS, id) ?? id,
    });
  }

  for (const id of f.searchTermKeys) {
    const parts = id.split('|');
    const termLabel = parts.length >= 3 ? parts.slice(2).join('|') : id;
    chips.push({
      dimension: 'searchTermKeys',
      id,
      label: termLabel.length > 40 ? `${termLabel.slice(0, 37)}…` : termLabel,
    });
  }

  for (const id of f.asins) {
    chips.push({
      dimension: 'asins',
      id,
      label: id,
    });
  }

  for (const id of f.adProductRowKeys) {
    chips.push({
      dimension: 'adProductRowKeys',
      id,
      label: id.length > 40 ? `${id.slice(0, 37)}…` : id,
    });
  }

  return chips;
}

export function WorkspaceFilterBar({
  graph,
  filter,
  onRemoveChip,
  onClearDimension,
  onClearAll,
  onRemoveStale,
}: {
  graph: EntityGraph;
  filter: EntityCrossFilter;
  onRemoveChip: (dimension: keyof EntityCrossFilter, id: string) => void;
  onClearDimension: (dimension: keyof EntityCrossFilter) => void;
  onClearAll: () => void;
  onRemoveStale: () => void;
}) {
  const chips = buildChips(graph, filter);
  const staleCount = chips.filter((c) => c.unavailable).length;

  if (chips.length === 0) return null;

  return (
    <div className="sticky top-0 z-20 rounded-xl border border-border bg-surface px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body-sm font-medium text-foreground-muted" role="status">
          {chips.length} filter{chips.length === 1 ? '' : 's'} active
        </span>
        {chips.map((chip) => (
          <button
            key={`${chip.dimension}-${chip.id}`}
            type="button"
            onClick={() => onRemoveChip(chip.dimension, chip.id)}
            className="inline-flex h-8 items-center gap-1.5 rounded-pill border border-brand/25 bg-brand-subtle px-3 text-body-sm text-foreground hover:border-brand"
            aria-label={`Remove filter ${chip.label}`}
          >
            <span className="text-foreground-subtle">{DIMENSION_PREFIX[chip.dimension]}:</span>
            <span className="font-medium">
              {chip.label}
              {chip.unavailable ? ' (unavailable)' : ''}
            </span>
            <X className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
          </button>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {staleCount > 0 ? (
            <button
              type="button"
              onClick={onRemoveStale}
              className="text-body-sm text-brand-deep hover:underline"
            >
              Remove stale ({staleCount})
            </button>
          ) : null}
          {normalizeCrossFilter(filter).campaignKeys.length > 0 ? (
            <button
              type="button"
              onClick={() => onClearDimension('campaignKeys')}
              className="h-8 rounded-pill px-3 text-body-sm text-foreground-muted hover:text-error"
            >
              Clear campaigns
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClearAll}
            className="h-8 rounded-pill px-3 text-body-sm text-foreground-muted hover:text-error"
          >
            Clear all
          </button>
        </div>
      </div>
    </div>
  );
}
